import { useState } from 'react';
import { motion } from 'framer-motion';
import { Avatar, Box, Checkbox, Row, Tooltip, Typography } from '@tempmee/react';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { type IBadge } from '~/domain/RecommendedProfessionals/recommendedProfessionalsTypes';
import { colors } from '@tempmee/tailwind';
import { EmbeddedRatingPopover } from './EmbeddedRatingPopover';

const ATTENDANCE_BADGE_TYPE = 'attendance-score';
const CHECKBOX_SIZE = 18;

interface IEmbeddedListItemProps {
  id: string;
  fullName: string;
  isFavorite: boolean;
  isAvailable: boolean;
  isSelected: boolean;
  profileImage: string;
  rating?: {
    averageRating: number;
    professionalSkills?: number;
    interpersonalSkills?: number;
    timeManagement?: number;
  };
  badges: IBadge[];
  onItemClick: (id: string) => void;
  onViewSkills: () => void;
}

interface IBadgeButtonProps {
  badge: IBadge;
  onShowPopup: () => void;
}

const BadgeButton = ({ badge, onShowPopup }: IBadgeButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <Tooltip
      title={badge.label}
      placement="bottom"

    >
      <Box
        as="span"
        role="button"
        aria-label={badge.label}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(e) => {
          e.stopPropagation();
          onShowPopup();
        }}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          filter: isHovered ? 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25))' : 'none',
          transition: 'filter 0.2s',
        }}
      >
        <Box
          as="span"
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundImage: `url(${badge.imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'block',
          }}
          aria-label={badge.label}
        />
      </Box>
    </Tooltip>
  );
};

export const EmbeddedListItem = ({
  id,
  fullName,
  isFavorite,
  isAvailable,
  isSelected,
  profileImage,
  rating,
  badges,
  onItemClick,
  onViewSkills,
}: IEmbeddedListItemProps) => {
  const [showBadgePopup, setShowBadgePopup] = useState(false);

  const attendanceBadge = badges.find((b) => b.type === ATTENDANCE_BADGE_TYPE);

  const handleClick = () => {
    onItemClick(id);
  };

  const handleViewSkills = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewSkills();
  };

  return (
    <>
      <motion.div
        role="presentation"
        layout
        onClick={handleClick}
        data-testid="embedded-list-item"
        data-cy={`invite-row-${id}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 0',
          borderBottom: `1px solid ${colors.neutral[100]}`,
          cursor: 'pointer',
        }}
      >
        <Row align="center" gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Box
            onClick={(e) => e.stopPropagation()}
            style={{
              flexShrink: 0,
              lineHeight: 0,
              width: CHECKBOX_SIZE,
              height: CHECKBOX_SIZE,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Checkbox
              checked={isSelected}
              onChange={() => onItemClick(id)}
              size="sm"
            />
          </Box>

          <Avatar
            size="lg"
            src={profileImage || undefined}
            alt={fullName}
            isFavorite={isFavorite}
            style={{ flexShrink: 0 }}
          />

          <Row align="center" gap={2} style={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1"
             
              color={colors.neutral[600]}
              style={{ maxWidth: 290, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {fullName}
            </Typography>
            {isAvailable ? (
              <>
                <Typography variant="body1" color={colors.neutral[500]}>
                  {'•'}
                </Typography>
                <Typography variant="subtitle1"
                 
                  color={colors.secondary[500]}
                  onClick={handleViewSkills}
                  data-cy={`invite-view-skills-${id}`}
                  style={{ cursor: 'pointer' }}
                >
                  View Skills
                </Typography>
              </>
            ) : (
              <Typography variant="body1" color={colors.error[500]}>
                Currently unavailable
              </Typography>
            )}
          </Row>
        </Row>

        <Row align="center" gap={3} style={{ flexShrink: 0 }}>
          {rating && (
            <EmbeddedRatingPopover
              averageRating={rating.averageRating}
              professionalSkills={rating.professionalSkills}
              interpersonalSkills={rating.interpersonalSkills}
              timeManagement={rating.timeManagement}
            />
          )}
          {attendanceBadge && (
            <BadgeButton badge={attendanceBadge} onShowPopup={() => setShowBadgePopup(true)} />
          )}
        </Row>
      </motion.div>

      {showBadgePopup && attendanceBadge && (
        <ConfirmPopup
          rightButtonAction={() => setShowBadgePopup(false)}
          rightButtonText="Got it!"
          title={
            <Box
              as="span"
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: 18,
              }}
            >
              <Typography variant="subtitle1"
                as="span"
               
                color={colors.neutral[600]}
                style={{ marginRight: 5 }}
              >
                {attendanceBadge.popupTitle}
              </Typography>
              {attendanceBadge.popupTitleIcon && (
                <Box
                  as="span"
                  role="img"
                  aria-label="icon"
                  style={{
                    width: 22,
                    height: 22,
                    backgroundImage: `url(${attendanceBadge.popupTitleIcon})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              )}
            </Box>
          }
          titleStyle={{ marginBottom: 5 }}
          description={attendanceBadge.popupDescription}
          Icon={() =>
            attendanceBadge.popupImageWeb ? (
              <Box
                as="span"
                role="img"
                aria-label="popup"
                style={{
                  display: 'block',
                  width: '50%',
                  aspectRatio: '1 / 1',
                  backgroundImage: `url(${attendanceBadge.popupImageWeb})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  marginInline: 'auto',
                }}
              />
            ) : null
          }
        />
      )}
    </>
  );
};
